import { Component, Inject, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ClassNames } from '../../../constants/class-names.constants';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { NavigationItem, NavigationState } from '../../../models/common';
import { ClaimService } from '../../../services/claim/claim.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';

@Component({
    selector: 'lsq-base-navigation',
    template: ''
})
export class LsqBaseNavigationComponent implements OnChanges {
    @Input() public navigationItems: NavigationItem[];
    @Input() public settingsDropdownNavigationItems: NavigationItem[];
    @Input() public hideNavigation = false;
    public state = new NavigationState();
    public isLoggedIn$ = this.userService.isLoggedIn$;
    public icons = Icons;
    public color = Color;
    public classNames = ClassNames;

    private _navLinkIndexesHovered: number[] = [];

    constructor(
        protected userService: UserService,
        @Inject(SETTINGS) protected settings: ISettings,
        protected router: Router,
        public featureFlagService: FeatureFlagService,
        public claimService: ClaimService,
        public responsiveService: ResponsiveService
    ) {}

    public ngOnChanges(): void {
        this._handleChanges();
    }

    public getHomeUrl(): string {
        return this.userService.getHomeUrl();
    }

    public async navigateToHome(): Promise<boolean> {
        if (!this.hideNavigation) {
            return this.userService.navigateToHome();
        }
        return false;
    }

    public isSomeNavLinkHovered(): boolean {
        return this._navLinkIndexesHovered.length > 0;
    }

    public handleNavLinkMouseEnter(index: number): void {
        if (this._navLinkIndexesHovered.indexOf(index) === -1) {
            this._navLinkIndexesHovered.push(index);
        }
    }

    public handleNavLinkMouseLeave(navItem: NavigationItem, index: number): void {
        const linkIndex = this._navLinkIndexesHovered.indexOf(index);
        if (
            !(navItem.showChildren && navItem.children && navItem.children.length) &&
            linkIndex > -1
        ) {
            this._navLinkIndexesHovered.splice(linkIndex, 1);
        }
    }

    public handleNavLinkKeyDown(
        event: KeyboardEvent,
        navItem: NavigationItem,
        index: number
    ): void {
        if (event.which === KeyCodes.Space) {
            if (this._navLinkIndexesHovered.indexOf(index) === -1) {
                this.showChildren(navItem, index, true);
                this.handleNavLinkMouseEnter(index);
            } else {
                this.showChildren(navItem, index, false);
                this.handleNavLinkMouseLeave(navItem, index);
            }
        }
    }

    public showChildren(navItem: NavigationItem, index: number, show = true): void {
        navItem.showChildren = show;
        if (navItem.showChildren && navItem.children && navItem.children.length) {
            this.handleNavLinkMouseEnter(index);
        } else {
            this.handleNavLinkMouseLeave(navItem, index);
        }
    }

    public getNavigationItemClassList(navItem: NavigationItem): string[] {
        const classes: string[] = [];
        if (navItem.showChildren && navItem.children && !!navItem.children.length) {
            classes.push(ClassNames.ShowingChildren);
        }
        return classes;
    }

    private _handleChanges(): void {
        let settingsDropdownNavigationItems: NavigationItem[] = [];
        if (
            this.settingsDropdownNavigationItems &&
            this.settingsDropdownNavigationItems.length > 0
        ) {
            settingsDropdownNavigationItems = [...this.settingsDropdownNavigationItems];
        }

        this.state.navigationItems = this.navigationItems;
        if (settingsDropdownNavigationItems && settingsDropdownNavigationItems.length > 0) {
            this.state.settingsDropdownNavigationItems = settingsDropdownNavigationItems;
            this.state.desktopSettingsDropdownNavigationItems = settingsDropdownNavigationItems.filter(
                (item) => !item.hideInDesktop
            );
        }
    }
}
